// "Where is this campaign live, and is it up to date?" rolled up across every
// simulator — what the list page's rows, coverage bar and tabs need, built on
// top of simulatorCampaignState's per-sim answer so they can't disagree either.
import {
  deriveSimulatorCampaignState,
  type CampaignForSimulatorState,
  type SimulatorForCampaignState,
} from "./simulatorCampaignState";

export interface CampaignCoverage {
  liveSimulatorIds: string[];
  /** Sims this campaign has reached but that aren't currently showing it — "Sent, not live" (W3). */
  sentNotLiveSimulatorIds: string[];
  /** Sims that are live but on an older version — the ones a "Push update" would reach. */
  updateReadySimulatorIds: string[];
  totalSimulatorCount: number;
  /** At least one sim is LIVE_OLDER_VERSION — "Update ready" in the list/detail. */
  hasUpdateReady: boolean;
  isLive: boolean;
}

export function buildCampaignCoverage(
  campaign: CampaignForSimulatorState,
  simulators: SimulatorForCampaignState[],
): CampaignCoverage {
  const states = simulators.map((simulator) => ({
    simulatorId: simulator.simulatorId,
    state: deriveSimulatorCampaignState(simulator, campaign),
  }));

  const idsWith = (relationship: string) =>
    states
      .filter(({ state }) => state.relationship === relationship)
      .map(({ simulatorId }) => simulatorId);

  const updateReadySimulatorIds = idsWith("LIVE_OLDER_VERSION");
  const liveSimulatorIds = [
    ...idsWith("LIVE_UP_TO_DATE"),
    ...updateReadySimulatorIds,
  ];

  return {
    liveSimulatorIds,
    sentNotLiveSimulatorIds: idsWith("SENT_NOT_LIVE"),
    updateReadySimulatorIds,
    totalSimulatorCount: simulators.length,
    hasUpdateReady: updateReadySimulatorIds.length > 0,
    isLive: liveSimulatorIds.length > 0,
  };
}

export type CampaignCoverageStatus =
  | "ARCHIVED"
  | "LIVE"
  | "SENT_NOT_LIVE"
  | "NOT_LIVE";

/**
 * Which of the four buckets a campaign is in — the list row and the detail
 * header badge both show this, in different wording, so the bucket rules
 * live here once rather than twice.
 */
export function classifyCampaignCoverageStatus(
  campaign: { deleted?: boolean },
  coverage: CampaignCoverage,
): CampaignCoverageStatus {
  if (campaign.deleted) return "ARCHIVED";
  if (coverage.isLive) return "LIVE";
  if (coverage.sentNotLiveSimulatorIds.length > 0) return "SENT_NOT_LIVE";
  return "NOT_LIVE";
}
